<template>

  <Card>
    <template #content>
    <div class="flex flex-col">
      <h1>New site definition</h1>

      <div :class="'spread'">

      <Button @click="helpDrawer.visible=true">?</Button>
      </div>
      <Divider />
    </div>
    
    <div>
      <div class="flex gap-2 items-center">
        <i class="pi pi-globe" style="font-size: 1.5rem"></i>
        <i class="pi pi-search" style="font-size: 1.5rem"></i>
      </div>
      <label for="location-search">Find a Place on the Map:</label>
      <p>Type a place name or address and press Enter to zoom the map to that location</p>
      <InputText 
        class="wide" 
        id="location-search" 
        v-model="locationSearch" 
        placeholder="Enter a location here" 
        @keyup.enter="searchLocation"
      />
      <Button @click="searchLocation" :disabled="isSearching">Search</Button>
      <small v-if="showWaitMessage" class="search-message">Please wait 1 second before searching again</small>

    </div>

    <div v-if="geocodingResults.length > 0" class="results-table">
      <DataTable :value="geocodingResults" class="p-datatable-sm">
        <Column field="display_name" header="Address"></Column>
        <Column field="lat" header="Latitude">
          <template #body="slotProps">
            {{ parseFloat(slotProps.data.lat).toFixed(3) }}
          </template>
        </Column>
        <Column field="lon" header="Longitude">
          <template #body="slotProps">
            {{ parseFloat(slotProps.data.lon).toFixed(3) }}
          </template>
        </Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button icon="pi pi-check" @click="selectLocation(slotProps.data)" />
          </template>
        </Column>
      </DataTable>
    </div>
    <div v-else-if="hasSearched" class="no-results">
      <p v-if="isSearching">Searching...</p>
      <p v-else>No results found</p>
    </div>


    <Divider />
    
    <div class="flex gap-2 items-center">
      <i class="pi pi-file-edit" style="font-size: 1.5rem"></i>
      <i class="pi pi-pencil" style="font-size: 1.5rem"></i>
    </div>


    <div>
      <label for="site-name">Site Name:</label>
      <!-- <input type="text" id="site-name" v-model="siteName" /> -->
      <InputText class="wide" id ="site-name" v-model="siteName" :invalid="!!siteName" placeholder="Enter name of site" />
    </div>

    <p>Define the extent of your new site:</p>
    <p>Draw a box on the map to define the site extent.</p>
    <div class="extent-actions">
      <Button @click="startDrawingEvent">Draw Site Extent</Button>
    </div>
    <div>
      <label for="lat-min">Latitude Min:</label>
      <InputNumber class="wide extent-locked-input" float id="lat-min" v-model="latMin" :invalid="!!latMin" placeholder="Enter minimum latitude" disabled />
      <!-- <input type="number" id="lat-min" v-model="latMin" /> -->
    </div>
    <div>
      <label for="lat-max">Latitude Max:</label>
      <InputNumber class="wide extent-locked-input" float id="lat-max" v-model="latMax" :invalid="!!latMax" placeholder="Enter maximum latitude" disabled />
    </div>
    <div>
      <label for="lon-min">Longitude Min:</label>
      <InputNumber class="wide extent-locked-input" float id="lon-min" v-model="lonMin" :invalid="!!lonMin" placeholder="Enter minimum longitude" disabled />
    </div>
    <div>
      <label for="lon-max">Longitude Max:</label>
      <InputNumber class="wide extent-locked-input" float id="lon-max" v-model="lonMax" :invalid="!!lonMax" placeholder="Enter maximum longitude" disabled />
    </div>
    <!-- Description -->
    <div>
      <p>Describe the site in a few words to let others know what work is being performed</p>
      
      <label for="description">Description:</label>
      <!-- <textarea id="description" v-model="description"></textarea> -->
      <Textarea class="wide" v-model="description" rows="5" />
    </div>
    <!-- <div>
      <label for="processing-scheme">Processing Scheme:</label>
      <select id="processing-scheme" v-model="processingScheme">
        <option value="PSI">PSI (latest version)</option>
        <option value="Amp">Amplitude Images (latest version)</option>
        <option value="DefaultAlert">Alerts (latest version)</option>
      </select>
    </div>
    <div>
      <label for="start-date">Start Date:</label>
      <input type="date" id="start-date" v-model="startDate" />
    </div>
    <div>
      <label for="end-date">End Date:</label>
      <input type="date" id="end-date" v-model="endDate" />
    </div> -->
    <Button @click="submitSite">Submit</Button>
  </template>
  </Card>
</template>


<script lang="ts">
import { defineComponent, onMounted, onActivated, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import { useSites } from '@/composables/useSites';
import { useMapStore } from '@/stores/mapStore'
import type { Site } from '@/composables/useSites'
import { useDrawer } from '@/composables/useDrawer';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

export default defineComponent({
  name: 'NewSite',
  props: {
    site_id: {
      type: String,
    },
    site_extent: {
      // number array of length 4 [latMin, latMax, lonMin, lonMax]
      type: Array,
      required: false,
      default: () => [null, null, null, null]
    },
    active: {
      type: Boolean,
      required: false,
      default: null
    }
  },
  data() {
    return {
      siteName: this.site_id
    }
  },
  watch: {
    site_id(newVal) {
      this.siteName = newVal;
    }
  },
  emits: ['startDrawing', 'stopDrawing', 'site-submitted'],
  setup(props, {emit}) {
    
    const sitesInterface = useSites()
    const helpDrawer = useDrawer()

    const drawButtonMessage = ref('Start Drawing')
    const drawButtonActive = ref(false)
    const startDrawingEvent = () => {
      // if (drawButtonActive.value) {
      //   drawButtonMessage.value = 'Draw Site Extent on Map'
      //   emit('stopDrawing')
      // } else {
        // drawButtonMessage.value = 'Stop Drawing Site Extent'
        // drawButtonActive.value = true
        emit('startDrawing')
      // }
    }

    

    onMounted(() => {
      console.log('NewSite mounted')
      // watch on props.active to show help drawer
      helpDrawer.header = 'New Site Help'
      helpDrawer.content = [
        "This page allows you to define a new site.",
        "Enter the site name, latitude and longitude values, and a description.",
        "Click 'Submit' to create the site."
      ]
      // helpDrawer.visible = true
    })

    console.log('NewSite active:', props.active)
    watch(() => props.active, (newVal) => {
        console.log('NewSite active', newVal)
        if (newVal) {
          helpDrawer.header = 'New Site Help'
          helpDrawer.content = [
            "This page allows you to define a new site.",
            "Enter the site name, latitude and longitude values, and a description.",
            "Click 'Submit' to create the site."
          ]
          helpDrawer.visible = true
        }
      })
    
    const router = useRouter()
    const siteName = ref('')
    // Set the siteName to siteId if one was passed in
    if (props.site_id) {
      if (props.site_id != "new") {
        siteName.value = props.site_id
      }
    }
    const latMin = ref<number | null>(null)
    const latMax = ref<number | null>(null)
    const lonMin = ref<number | null>(null)
    const lonMax = ref<number | null>(null)

    // Watch for changes in site_extent and update values accordingly
    watch(() => props.site_extent, (newExtent) => {
      if (newExtent && newExtent.length === 4) {
        // check type of newExtent
        if (!Array.isArray(newExtent) || newExtent.length !== 4 || !newExtent.every(num => typeof num === 'number')) {
          throw new Error(`Invalid site_extent: expected [number, number, number, number], got ${newExtent}`);
        }

        [latMin.value, latMax.value, lonMin.value, lonMax.value] = newExtent as [number, number, number, number];
      } else {
        latMin.value = latMax.value = lonMin.value = lonMax.value = null;
      }
    }, { immediate: true }); // Runs immediately in case props already have values


    const description = ref('')
    const processingScheme = ref('')
    const startDate = ref('')
    const endDate = ref('')

    const locationSearch = ref('')
    const geocodingResults = ref<any[]>([])
    const isSearching = ref(false)
    const hasSearched = ref(false)
    const showWaitMessage = ref(false)
    let lastSearchTime = 0

    const searchLocation = async () => {
      if (!locationSearch.value || isSearching.value) return;
      
      // Check if we've searched in the last second
      const now = Date.now()
      if (now - lastSearchTime < 1000) {
        showWaitMessage.value = true
        setTimeout(() => {
          showWaitMessage.value = false
        }, 2000) // Hide message after 2 seconds
        return
      }
      
      isSearching.value = true
      hasSearched.value = true
      lastSearchTime = now
      
      try {
        const response = await fetch(`/api/geocoding/?q=${encodeURIComponent(locationSearch.value)}`);
        const data = await response.json();
        console.log('Geocoding results:', data);
        geocodingResults.value = data;
      } catch (error) {
        console.error('Error searching location:', error);
        geocodingResults.value = [];
      } finally {
        isSearching.value = false
      }
    }

    const selectLocation = (location: any) => {
      // Get the bounding box from the result
      const boundingBox = {
        latMin: parseFloat(location.boundingbox[0]),
        latMax: parseFloat(location.boundingbox[1]),
        lonMin: parseFloat(location.boundingbox[2]),
        lonMax: parseFloat(location.boundingbox[3])
      };
      
      // Set the map view to the bounding box
      const mapStore = useMapStore();
      mapStore.setView(boundingBox);
      
      // Set the site name if it's empty
      if (!siteName.value) {
        siteName.value = locationSearch.value;
      }
      
      // Clear the results and reset search state
      geocodingResults.value = [];
      hasSearched.value = false;
    }

    const submitSite = async () => {
      console.log('Submit site:', siteName.value, latMin.value, latMax.value, lonMin.value, lonMax.value, processingScheme.value, startDate.value, endDate.value)
      // Call your API to submit the site
      // Redirect to the site view at /site/:siteId


      const typedSite: Site = {
        name: siteName.value,
        geometry: `[${latMax.value}, ${lonMax.value}, ${latMin.value}, ${lonMin.value}]`,
        description: description.value,
        visible: true,
      }
      await sitesInterface.postNewSite(typedSite);
      // router.push(`/site/${siteName.value}`)
      emit('site-submitted', siteName.value) // emit event to parent component
    }

    // Query the site details if a siteId was passed in
    const populateSiteDetails = async () => {
      if (props.site_id) {
        const sitesConnector = useSites()
        await sitesConnector.fetchItems()
        console.log(sitesConnector.sites.value)
        const site = sitesConnector.sites.value.find((site) => site.name === props.site_id)
        if (site) {

          // get description
          description.value = site.description ? site.description as string : description.value

          // might not have a latMin field...
          // latMin.value = site.latMin ? site.latMin as number : latMin.value;
          // latMax.value = site.latMax ? site.latMax as number : latMax.value;
          // lonMin.value = site.lonMin ? site.lonMin as number : lonMin.value;
          // lonMax.value = site.lonMax ? site.lonMax as number : lonMax.value;
          // site.latMin && (latMin.value = site.latMin as number)
          // site.latMax && (latMax.value = site.latMax as number)
          // site.lonMin && (lonMin.value = site.lonMin as number)
          // site.lonMax && (lonMax.value = site.lonMax as number)

          // might be using a 'geometry' field instead...
          // if (!site.latMin && site?.geometry) {
          const geometryString = 'geometry' in site ? site.geometry : ''
          //   console.log(geometryString)
            // split the geometry string :
            // [51.2900900900901, -2.986224797142688, 51.10990990990991, -3.273775202857312]
            // -> 
            //strip []
            const geometryValuesAsString = geometryString.slice(1, -1)
            const coords = geometryValuesAsString.split(',').map((coord) => parseFloat(coord))
            latMax.value = coords[0]
            lonMax.value = coords[1]
            latMin.value = coords[2]
            lonMin.value = coords[3]
            console.log(coords)
            console.log(coords[0])
            console.log(latMax.value)
          // }

          // if we have valid lat/lon values...
          if (latMin.value && latMax.value && lonMin.value && lonMax.value) {
            const boundingBox = {
              latMin: latMin.value,
              latMax: latMax.value,
              lonMin: lonMin.value,
              lonMax: lonMax.value
            }
            // set the map extent
            const mapStore = useMapStore()
            mapStore.setView(boundingBox)
            mapStore.drawBox(boundingBox)
          }
        }
        console.log(site)


      }
    }

    populateSiteDetails()

    return {
      siteName,
      latMin,
      latMax,
      lonMin,
      lonMax,
      processingScheme,
      startDate,
      endDate,
      description,
      submitSite,
      helpDrawer,
      drawButtonMessage,
      drawButtonActive,
      startDrawingEvent,
      locationSearch,
      searchLocation,
      geocodingResults,
      selectLocation,
      isSearching,
      hasSearched,
      showWaitMessage
    }
  }
})
</script>

<style scoped>
.centre-flex-for-site {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.vertical-stack {
  margin-bottom: 10px;
  flex-direction: column;
}

.wide {
  width: 100%;
}

:deep(.spread) {
  display: flex;
  justify-content: space-between;
}

.results-table {
  margin: 1rem 0;
  max-height: 300px;
  overflow-y: auto;
}

.search-message {
  color: var(--text-color-secondary);
  margin-left: 0.5rem;
}

.no-results {
  padding: 1rem;
  text-align: center;
  color: var(--text-color-secondary);
}

.extent-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

:deep(.extent-locked-input .p-inputnumber-input) {
  opacity: 0.6;
}
</style>